import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Building,
  User,
  Plane,
  ArrowRight,
  ArrowLeft,
  Home,
  Calendar,
  Users,
  Target,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from "lucide-react";
import { createLead, getPublicFaq } from "@/lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1760372060494-bee4f8441ca6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxmcmVuY2glMjBjb3VudHJ5c2lkZSUyMHZpbGxhZ2UlMjBjb3p5JTIwc3RyZWV0JTIwd2FybXxlbnwwfHx8fDE3NzQwMjU4Mzd8MA&ixlib=rb-4.1.0&q=85";

const PROFILES = [
  {
    value: "proprietaire",
    label: "Propriétaire",
    desc: "Je souhaite confier la gestion de mon bien",
    icon: Building,
    color: "from-[#004333] to-[#006B52]",
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=600&q=80",
  },
  {
    value: "investisseur",
    label: "Investisseur",
    desc: "Je cherche un accompagnement pour mes projets",
    icon: Target,
    color: "from-[#C9922A] to-[#E8C874]",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80",
  },
  {
    value: "voyageur",
    label: "Voyageur",
    desc: "J'ai une question sur un séjour ou une réservation",
    icon: Plane,
    color: "from-[#1A120B] to-[#2C1E14]",
    image: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=600&q=80",
  },
];

// Dynamic questions per profile
const PROFILE_QUESTIONS = {
  proprietaire: [
    { key: "nb_biens", label: "Combien de biens souhaitez-vous confier ?", type: "select", options: ["1 bien", "2-3 biens", "4+ biens"] },
    { key: "ville", label: "Dans quelle ville ?", type: "select", options: ["Limoges", "Évaux-les-Bains", "Autre"] },
    { key: "objectif", label: "Votre objectif principal ?", type: "select", options: ["Loyer garanti (Sérénité)", "Gestion locative (Partenariat)", "Je ne sais pas encore"] },
  ],
  investisseur: [
    { key: "projet", label: "Quel est votre projet ?", type: "select", options: ["Achat pour location courte durée", "Optimisation d'un bien existant", "Investissement locatif clé en main"] },
    { key: "budget", label: "Votre budget estimé ?", type: "select", options: ["Moins de 100 000 EUR", "100 000 - 200 000 EUR", "200 000 - 500 000 EUR", "Plus de 500 000 EUR"] },
    { key: "echeance", label: "Votre échéance ?", type: "select", options: ["Dans les 3 mois", "Dans les 6 mois", "Dans l'année", "Je me renseigne"] },
  ],
  voyageur: [
    { key: "destination", label: "Quelle destination vous intéresse ?", type: "select", options: ["Limoges", "Évaux-les-Bains", "Les deux"] },
    { key: "voyageurs", label: "Nombre de voyageurs ?", type: "select", options: ["1-2 personnes", "3-4 personnes", "5+ personnes"] },
    { key: "periode", label: "Quand souhaitez-vous séjourner ?", type: "select", options: ["Ce mois-ci", "Dans 1-3 mois", "Je n'ai pas encore de dates"] },
  ],
};

// Slide animation variants
const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
};

export default function ContactPage() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [formData, setFormData] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    getPublicFaq().then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  const goNext = () => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, 3));
  };
  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    setDynamicAnswers({});
    setTimeout(goNext, 300);
  };

  const handleSubmit = async () => {
    if (!formData.nom || !formData.email) {
      toast.error("Veuillez renseigner votre nom et email");
      return;
    }
    setLoading(true);
    try {
      await createLead({
        type_lead: selectedProfile,
        ...formData,
        message: `[${PROFILES.find((p) => p.value === selectedProfile)?.label}] ${Object.entries(dynamicAnswers).map(([k, v]) => `${k}: ${v}`).join(" | ")} --- ${formData.message}`,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setDirection(-1);
    setSelectedProfile(null);
    setDynamicAnswers({});
    setFormData({ nom: "", email: "", telephone: "", message: "" });
    setSubmitted(false);
  };

  const questions = selectedProfile ? PROFILE_QUESTIONS[selectedProfile] || [] : [];
  const allQuestionsAnswered = questions.every((q) => dynamicAnswers[q.key]);
  const profileData = PROFILES.find((p) => p.value === selectedProfile);

  return (
    <div data-testid="contact-page">
      {/* ===================== HERO ===================== */}
      <section className="relative min-h-[40vh] flex items-end overflow-hidden" data-testid="contact-hero">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1008]/90 via-[#1a1008]/40 to-[#1a1008]/10" />
        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 pt-32">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3">
            Contact
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Commençons l'aventure
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-white/60 mt-4 max-w-2xl text-lg">
            Une question ? Un projet ? Ne laissez pas votre logement vide ou mal géré. 
            Contactez-nous et nous vous recontacterons sous 24 h pour en discuter.
          </motion.p>
        </div>
      </section>

      {/* ===================== MULTI-STEP FORM ===================== */}
      <section className="py-16 md:py-20 bg-[#1A120B]" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Progress Bar */}
          {!submitted && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                {["Votre profil", "Votre projet", "Vos coordonnées"].map((label, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${
                        step > i + 1
                          ? "bg-[#C9922A] text-white"
                          : step === i + 1
                          ? "bg-[#C9922A]/20 border-2 border-[#C9922A] text-[#C9922A]"
                          : "bg-white/5 border border-white/15 text-white/30"
                      }`}
                      data-testid={`step-indicator-${i + 1}`}
                    >
                      {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium uppercase tracking-wider hidden sm:inline ${
                      step === i + 1 ? "text-[#C9922A]" : step > i + 1 ? "text-white/50" : "text-white/20"
                    }`}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#C9922A] to-[#E8C874] rounded-full"
                  initial={false}
                  animate={{ width: `${(step / 3) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          {/* Form Steps */}
          <div className="relative overflow-hidden min-h-[380px]">
            <AnimatePresence mode="wait" custom={direction}>
              {/* ===== STEP 1: Profile Selection ===== */}
              {step === 1 && !submitted && (
                <motion.div
                  key="step1"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                  <h2 className="font-display text-2xl text-white mb-2">Qui êtes-vous ?</h2>
                  <p className="text-white/40 mb-8 text-sm">Sélectionnez votre profil pour personnaliser votre demande.</p>

                  <div className="grid sm:grid-cols-3 gap-4">
                    {PROFILES.map((profile) => {
                      const Icon = profile.icon;
                      const isSelected = selectedProfile === profile.value;
                      return (
                        <motion.button
                          key={profile.value}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleProfileSelect(profile.value)}
                          className={`relative group text-left rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                            isSelected
                              ? "border-[#C9922A] shadow-[0_0_30px_rgba(201,146,42,0.15)]"
                              : "border-white/10 hover:border-white/25"
                          }`}
                          data-testid={`profile-${profile.value}`}
                        >
                          {/* Background image */}
                          <div className="aspect-[3/4] relative overflow-hidden">
                            <img
                              src={profile.image}
                              alt={profile.label}
                              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className={`absolute inset-0 bg-gradient-to-t ${profile.color} opacity-70`} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                            {/* Content */}
                            <div className="absolute inset-0 p-5 flex flex-col justify-between">
                              <div className={`w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center ${
                                isSelected ? "bg-[#C9922A]/30 border-[#C9922A]/50" : ""
                              }`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h3 className="font-display text-lg text-white mb-1">{profile.label}</h3>
                                <p className="text-white/50 text-xs leading-relaxed">{profile.desc}</p>
                              </div>
                            </div>

                            {/* Selected indicator */}
                            {isSelected && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#C9922A] flex items-center justify-center"
                              >
                                <Check className="w-3 h-3 text-white" />
                              </motion.div>
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* ===== STEP 2: Dynamic Questions ===== */}
              {step === 2 && !submitted && (
                <motion.div
                  key="step2"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                  <h2 className="font-display text-2xl text-white mb-2">Parlez-nous de votre projet</h2>
                  <p className="text-white/40 mb-8 text-sm">
                    Quelques précisions pour mieux vous accompagner.
                  </p>

                  <div className="space-y-5">
                    {questions.map((q, i) => (
                      <motion.div
                        key={q.key}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <label className="block text-sm font-medium text-white/60 mb-3">{q.label}</label>
                        <div className="flex flex-wrap gap-2">
                          {q.options.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setDynamicAnswers({ ...dynamicAnswers, [q.key]: opt })}
                              className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-200 ${
                                dynamicAnswers[q.key] === opt
                                  ? "bg-[#C9922A]/15 border-[#C9922A] text-[#C9922A]"
                                  : "bg-white/[0.03] border-white/10 text-white/50 hover:border-white/25 hover:text-white/80"
                              }`}
                              data-testid={`q-${q.key}-${opt.substring(0, 10).toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="flex justify-between mt-10">
                    <button
                      onClick={goBack}
                      className="flex items-center gap-2 px-5 py-2.5 text-white/40 hover:text-white transition-colors text-sm"
                      data-testid="step2-back"
                    >
                      <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <button
                      onClick={goNext}
                      disabled={!allQuestionsAnswered}
                      className="flex items-center gap-2 px-6 py-3 bg-[#C9922A] text-white rounded-full font-semibold text-sm hover:bg-[#B8831F] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="step2-next"
                    >
                      Continuer <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ===== STEP 3: Contact Info ===== */}
              {step === 3 && !submitted && (
                <motion.div
                  key="step3"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                >
                  <h2 className="font-display text-2xl text-white mb-2">Vos coordonnées</h2>
                  <p className="text-white/40 mb-8 text-sm">
                    Pour que nous puissions vous recontacter rapidement.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Nom complet *</label>
                      <input
                        type="text"
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        placeholder="Votre nom"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/20 focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors text-sm"
                        data-testid="contact-nom"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="votre@email.fr"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/20 focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors text-sm"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="+33 7 67 48 43 13"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/20 focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors text-sm"
                      data-testid="contact-telephone"
                    />
                  </div>
                  <div className="mb-8">
                    <label className="block text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Message</label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre projet ou posez votre question..."
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-white/20 focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none transition-colors text-sm resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={goBack}
                      className="flex items-center gap-2 px-5 py-2.5 text-white/40 hover:text-white transition-colors text-sm"
                      data-testid="step3-back"
                    >
                      <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] rounded-full font-bold text-sm hover:shadow-[0_4px_24px_rgba(201,146,42,0.4)] transition-all disabled:opacity-50"
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-[#1A120B]/30 border-t-[#1A120B] rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" /> Envoyer ma demande
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ===== SUCCESS STATE ===== */}
              {submitted && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col items-center justify-center text-center py-10"
                  data-testid="contact-success"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                    className="w-20 h-20 rounded-full bg-[#C9922A]/15 border-2 border-[#C9922A]/30 flex items-center justify-center mb-6"
                  >
                    <Sparkles className="w-9 h-9 text-[#C9922A]" />
                  </motion.div>
                  <h2 className="font-display text-3xl text-white mb-3">Merci !</h2>
                  <p className="text-white/50 max-w-md mb-8 leading-relaxed">
                    Votre demande a été envoyée avec succès. Un conseiller Happyhouse conciergerie vous recontactera sous 24h.
                  </p>
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-6 py-3 border border-white/15 text-white/60 rounded-full text-sm hover:bg-white/5 transition-colors"
                    data-testid="contact-reset"
                  >
                    <ArrowLeft className="w-4 h-4" /> Envoyer une nouvelle demande
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ===================== CONTACT INFO CARDS ===================== */}
      <section className="py-16 bg-[#F5F0EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: Phone, label: "Téléphone", value: "+33 7 67 48 43 13", href: "tel:+33767484313" },
              { icon: Mail, label: "Email", value: "contact@happyhouse-conciergerie.fr", href: "mailto:contact@happyhouse-conciergerie.fr" },
              { icon: MapPin, label: "Zone d'intervention", value: "Limoges & Évaux-les-Bains", href: null },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                {item.href ? (
                  <a
                    href={item.href}
                    className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-[#E0D6CA] shadow-sm hover:shadow-md hover:border-[#C9922A]/30 transition-all group"
                    data-testid={`contact-card-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#004333] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9922A] transition-colors">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B5E50] uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="font-semibold text-[#004333] text-sm">{item.value}</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-[#E0D6CA] shadow-sm" data-testid={`contact-card-${i}`}>
                    <div className="w-11 h-11 rounded-xl bg-[#004333] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-[#6B5E50] uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="font-semibold text-[#004333] text-sm">{item.value}</p>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      {faqs.length > 0 && (
        <section className="py-16 bg-[#EDE8E1]" data-testid="contact-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3 block">FAQ</span>
              <h2 className="font-display text-2xl md:text-3xl text-[#004333]">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div key={faq.id} className="bg-[#FFFDF9] border border-[#E0D6CA] rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-[#EDE8E1]/50 transition-colors"
                    data-testid={`faq-${faq.id}`}
                  >
                    <span className="font-semibold text-[#004333] pr-4 text-sm">{faq.question}</span>
                    {openFaq === faq.id ? (
                      <ChevronUp className="w-4 h-4 text-[#C9922A] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#C9922A] flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === faq.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-5 pb-5">
                      <p className="text-[#6B5E50] leading-relaxed text-sm border-t border-[#E8E0D4] pt-3">{faq.reponse}</p>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
